import React, { useEffect, useMemo, useRef, useState } from "react";
import RestaurantCard, { withPromotedLabel } from "./RestaurantCard";
import Shimmer from "./Shimmer";
import useOnlineStatus from "../utils/useOnlineStatus";
import useDebouncedValue from "../utils/useDebouncedValue";
import { useListRestaurantsQuery } from "../utils/restaurantApi";
import { readApiError } from "../utils/apiSlice";
import "./Home.css";

// Built once at module scope. Creating it inside the component would make a
// brand new component type on every render, remounting every card.
const VegRestaurantCard = withPromotedLabel(RestaurantCard);

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

const TOP_RATING = 4.5;
const FAST_DELIVERY_MINS = 30;

// Price bands as API filters, in paise (₹1 = 100). Bounds are inclusive.
const COST_BANDS = {
  any: {},
  budget: { minCostForTwoPaise: 1, maxCostForTwoPaise: 29_999 },
  mid: { minCostForTwoPaise: 30_000, maxCostForTwoPaise: 50_000 },
  premium: { minCostForTwoPaise: 50_001 },
};

const COST_LABELS = {
  any: "Any price",
  budget: "Under ₹300",
  mid: "₹300 to ₹500",
  premium: "Over ₹500",
};

// UI sort keys → the API's `sort` values
const SORTS = {
  relevance: "relevance",
  rating: "rating",
  delivery: "delivery_time",
  costAsc: "cost_asc",
  costDesc: "cost_desc",
};

const SORT_LABELS = {
  relevance: "Featured",
  rating: "Rating: high to low",
  delivery: "Fastest delivery",
  costAsc: "Cost: low to high",
  costDesc: "Cost: high to low",
};

const DEFAULT_FILTERS = {
  diet: "all", // all | veg | nonveg
  cuisine: "all",
  cost: "any",
  topRated: false,
  fastDelivery: false,
  sort: "relevance",
};

const DIET_TO_PURE_VEG = { all: undefined, veg: true, nonveg: false };

// The UI's filter state → GET /restaurants query params (no cursor).
const toQueryArgs = (filters, search) => ({
  q: search || undefined,
  cuisine: filters.cuisine === "all" ? undefined : filters.cuisine,
  pureVeg: DIET_TO_PURE_VEG[filters.diet],
  minRating: filters.topRated ? TOP_RATING : undefined,
  maxDeliveryMinutes: filters.fastDelivery ? FAST_DELIVERY_MINS : undefined,
  ...COST_BANDS[filters.cost],
  sort: SORTS[filters.sort],
  limit: PAGE_SIZE,
});

function Home() {
  const [searchText, setSearchText] = useState("");
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const searchRef = useRef(null);
  const isOnline = useOnlineStatus();

  // Typing "biryani" should be one request, not seven.
  const search = useDebouncedValue(searchText.trim(), SEARCH_DEBOUNCE_MS);

  const baseArgs = useMemo(() => toQueryArgs(filters, search), [filters, search]);
  const filterKey = JSON.stringify(baseArgs);

  // "Load more" position, remembered together with the filter set it
  // belongs to. A cursor from other filters is simply ignored, so a stale
  // cursor is never sent with new filters (a reset in useEffect would run
  // one render too late and send exactly that request).
  const [paging, setPaging] = useState({ key: filterKey, cursor: undefined });
  const cursor = paging.key === filterKey ? paging.cursor : undefined;

  const {
    data,
    currentData,
    error,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useListRestaurantsQuery({ ...baseArgs, cursor }, { skip: !isOnline });

  // `data` keeps the previous filters' results while new ones load, so
  // the grid dims instead of flashing empty; `currentData` is only ever
  // the result for exactly these filters.
  const isRefreshing = isFetching && !cursor && currentData === undefined;
  const isLoadingMore = isFetching && Boolean(cursor);
  const loadMoreFailed = isError && Boolean(cursor);

  // "/" jumps to the search box, the way most catalogue UIs behave.
  useEffect(() => {
    const handleShortcut = (event) => {
      if (event.key !== "/" || event.metaKey || event.ctrlKey) return;

      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      event.preventDefault();
      searchRef.current?.focus();
    };

    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  }, []);

  const activeFilterCount = Object.keys(DEFAULT_FILTERS).filter(
    (key) => key !== "sort" && filters[key] !== DEFAULT_FILTERS[key],
  ).length;

  const trimmedQuery = searchText.trim();
  const hasRefinements = activeFilterCount > 0 || Boolean(trimmedQuery);

  const resetAll = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchText("");
  };

  const updateFilter = (key, value) =>
    setFilters((current) => ({ ...current, [key]: value }));

  const toggleFilter = (key) =>
    setFilters((current) => ({ ...current, [key]: !current[key] }));

  const loadMore = () => {
    if (data?.nextCursor) setPaging({ key: filterKey, cursor: data.nextCursor });
  };

  if (!isOnline) {
    return (
      <div className="home">
        <div className="home-state">
          <h2>You&apos;re offline</h2>
          <p>
            Check your internet connection. The restaurant list will come back
            as soon as you reconnect.
          </p>
        </div>
      </div>
    );
  }

  if (isLoading) return <Shimmer />;

  // A failure with nothing to show yet: say so, and offer a retry.
  if (isError && !cursor && !currentData) {
    const { message } = readApiError(error);
    return (
      <div className="home">
        <div className="home-state" role="alert">
          <h2>Couldn&apos;t load restaurants</h2>
          <p>{message}</p>
          <button type="button" onClick={refetch} disabled={isFetching}>
            {isFetching ? "Retrying…" : "Try again"}
          </button>
        </div>
      </div>
    );
  }

  const restaurants = data?.restaurants ?? [];
  const total = data?.total ?? 0;
  const cuisineOptions = data?.facets?.cuisines ?? [];
  const remaining = total - restaurants.length;

  return (
    <div className="home">
      <div className="home-searchbar">
        <span className="home-search-icon" aria-hidden="true">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path
              d="m20 20-3.6-3.6"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </span>

        <input
          ref={searchRef}
          type="text"
          className="home-search-input"
          placeholder="Search for a restaurant, cuisine or area"
          aria-label="Search for a restaurant, cuisine or area"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") setSearchText("");
          }}
          autoComplete="off"
          maxLength={100}
        />

        {searchText ? (
          <button
            type="button"
            className="home-search-clear"
            onClick={() => setSearchText("")}
            aria-label="Clear search"
          >
            &times;
          </button>
        ) : (
          <kbd className="home-search-kbd" aria-hidden="true">
            /
          </kbd>
        )}
      </div>

      <div className="home-filters">
        <div className="segmented" role="group" aria-label="Dietary preference">
          {[
            { value: "all", label: "All" },
            { value: "veg", label: "Pure Veg" },
            { value: "nonveg", label: "Non-veg" },
          ].map(({ value, label }) => (
            <button
              key={value}
              type="button"
              className={filters.diet === value ? "is-active" : ""}
              aria-pressed={filters.diet === value}
              onClick={() => updateFilter("diet", value)}
            >
              {label}
            </button>
          ))}
        </div>

        <button
          type="button"
          className={`filter-chip ${filters.topRated ? "is-active" : ""}`}
          aria-pressed={filters.topRated}
          onClick={() => toggleFilter("topRated")}
        >
          Rated {TOP_RATING}+
        </button>

        <button
          type="button"
          className={`filter-chip ${filters.fastDelivery ? "is-active" : ""}`}
          aria-pressed={filters.fastDelivery}
          onClick={() => toggleFilter("fastDelivery")}
        >
          Under {FAST_DELIVERY_MINS} mins
        </button>

        <label className="filter-select">
          <span className="sr-only">Cuisine</span>
          <select
            value={filters.cuisine}
            onChange={(event) => updateFilter("cuisine", event.target.value)}
          >
            <option value="all">All cuisines</option>
            {cuisineOptions.map(({ name, count }) => (
              <option key={name} value={name}>
                {name} ({count})
              </option>
            ))}
          </select>
        </label>

        <label className="filter-select">
          <span className="sr-only">Price for two</span>
          <select
            value={filters.cost}
            onChange={(event) => updateFilter("cost", event.target.value)}
          >
            {Object.entries(COST_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <label className="filter-select is-sort">
          <span className="sr-only">Sort by</span>
          <select
            value={filters.sort}
            onChange={(event) => updateFilter("sort", event.target.value)}
          >
            {Object.entries(SORT_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="home-resultbar">
        <p aria-live="polite">
          <strong>{total}</strong> {total === 1 ? "restaurant" : "restaurants"}
          {search && <> for &ldquo;{search}&rdquo;</>}
          {isRefreshing && <span className="home-updating"> · updating…</span>}
        </p>

        {hasRefinements && (
          <button type="button" className="home-reset" onClick={resetAll}>
            Reset{activeFilterCount > 0 && ` (${activeFilterCount})`}
          </button>
        )}
      </div>

      {restaurants.length === 0 && !isRefreshing ? (
        <div className="home-state">
          <h2>No restaurants match</h2>
          <p>Try widening the price range or clearing a filter or two.</p>
          <button type="button" onClick={resetAll}>
            Reset everything
          </button>
        </div>
      ) : (
        <>
          <ul
            className={`home-grid ${isRefreshing ? "is-refreshing" : ""}`}
            aria-busy={isRefreshing}
          >
            {restaurants.map((restaurant) => {
              const Card = restaurant.isPureVeg ? VegRestaurantCard : RestaurantCard;

              return (
                <li key={restaurant.id}>
                  <Card restaurant={restaurant} />
                </li>
              );
            })}
          </ul>

          {loadMoreFailed && (
            <p className="home-loadmore-error" role="alert">
              {readApiError(error).message}
            </p>
          )}

          {(data?.nextCursor || loadMoreFailed) && !isRefreshing && (
            <div className="home-loadmore">
              <button
                type="button"
                onClick={loadMoreFailed ? refetch : loadMore}
                disabled={isLoadingMore}
              >
                {isLoadingMore
                  ? "Loading…"
                  : loadMoreFailed
                    ? "Try again"
                    : `Show more restaurants (${remaining})`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default Home;
